/**
 * Professional Appointment Status System
 * 
 * This module defines a comprehensive, standardized appointment status system
 * that follows professional dental practice management standards.
 * 
 * Features:
 * - Consistent status terminology across all components
 * - Professional status flow management
 * - Auto-status detection rules
 * - Grace periods and transition validation
 * - Comprehensive styling configuration
 */

export type AppointmentStatus = 
  | 'pending'      // Just booked, needs confirmation
  | 'confirmed'    // Patient confirmed attendance
  | 'checked-in'   // Patient arrived (optional intermediate state)
  | 'in-progress'  // Currently happening
  | 'completed'    // Successfully finished
  | 'cancelled'    // Cancelled before time
  | 'no-show'      // Patient didn't arrive
  | 'rescheduled'; // Moved to different time

export interface AppointmentStatusConfig {
  label: string;
  shortLabel?: string; // For compact displays
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  canTransitionTo: AppointmentStatus[];
  autoTrigger?: {
    condition: 'time-passed' | 'grace-period-expired' | 'reminder-due';
    delay?: number; // minutes after appointment time
    triggerStatus?: AppointmentStatus; // What status to auto-set
  };
  isActive: boolean; // Whether appointment is still active/changeable
  priority: number; // For sorting and display order
  requiresConfirmation?: boolean; // Whether status change needs user confirmation
  allowManualSet?: boolean; // Whether staff can manually set this status
  notificationTriggers?: {
    patient?: boolean; // Send notification to patient
    staff?: boolean; // Send notification to staff
    system?: boolean; // Log system event
  };
}

export const APPOINTMENT_STATUS_CONFIG: Record<AppointmentStatus, AppointmentStatusConfig> = {
  'pending': {
    label: 'Pending Confirmation',
    shortLabel: 'Pending',
    description: 'Appointment booked but not yet confirmed by patient',
    color: 'rgb(var(--badge-warning-text))',
    bgColor: 'rgb(var(--badge-warning-bg))',
    borderColor: 'rgb(var(--badge-warning-border))',
    canTransitionTo: ['confirmed', 'cancelled'],
    isActive: true,
    priority: 1,
    allowManualSet: true,
    autoTrigger: {
      condition: 'reminder-due',
      delay: 1440, // 24 hours before appointment
    },
    notificationTriggers: {
      patient: true,
      staff: false,
      system: true
    }
  },
  'confirmed': {
    label: 'Confirmed',
    shortLabel: 'Confirmed',
    description: 'Patient has confirmed they will attend',
    color: 'rgb(var(--badge-info-text))',
    bgColor: 'rgb(var(--badge-info-bg))',
    borderColor: 'rgb(var(--badge-info-border))',
    canTransitionTo: ['checked-in', 'in-progress', 'cancelled', 'no-show', 'rescheduled'],
    isActive: true,
    priority: 2,
    allowManualSet: true,
    notificationTriggers: {
      patient: false,
      staff: true,
      system: true
    }
  },
  'checked-in': {
    label: 'Patient Arrived',
    shortLabel: 'Arrived',
    description: 'Patient has arrived and checked in',
    color: 'rgb(var(--badge-success-text))',
    bgColor: 'rgb(var(--badge-success-bg))',
    borderColor: 'rgb(var(--badge-success-border))',
    canTransitionTo: ['in-progress', 'cancelled'],
    isActive: true,
    priority: 3,
    allowManualSet: true,
    notificationTriggers: {
      patient: false,
      staff: true,
      system: true
    }
  },
  'in-progress': {
    label: 'In Progress',
    shortLabel: 'Active',
    description: 'Treatment is currently in progress',
    color: 'rgb(var(--badge-primary-text))',
    bgColor: 'rgb(var(--badge-primary-bg))',
    borderColor: 'rgb(var(--badge-primary-border))',
    canTransitionTo: ['completed', 'cancelled'],
    isActive: true,
    priority: 4,
    allowManualSet: true,
    requiresConfirmation: false, // Can be set quickly during treatment
    notificationTriggers: {
      patient: false,
      staff: true,
      system: true
    }
  },
  'completed': {
    label: 'Completed',
    shortLabel: 'Done',
    description: 'Treatment successfully completed',
    color: 'rgb(var(--badge-success-text))',
    bgColor: 'rgb(var(--badge-success-bg))',
    borderColor: 'rgb(var(--badge-success-border))',
    canTransitionTo: [], // Final state - no transitions allowed
    isActive: false,
    priority: 5,
    allowManualSet: true,
    requiresConfirmation: true,
    notificationTriggers: {
      patient: true, // Send completion confirmation
      staff: false,
      system: true
    }
  },
  'cancelled': {
    label: 'Cancelled',
    shortLabel: 'Cancelled',
    description: 'Appointment was cancelled before treatment',
    color: 'rgb(var(--badge-error-text))',
    bgColor: 'rgb(var(--badge-error-bg))',
    borderColor: 'rgb(var(--badge-error-border))',
    canTransitionTo: [], // Final state - but can create new appointment
    isActive: false,
    priority: 6,
    allowManualSet: true,
    requiresConfirmation: true,
    notificationTriggers: {
      patient: true,
      staff: true,
      system: true
    }
  },
  'no-show': {
    label: 'No Show',
    shortLabel: 'No-Show',
    description: 'Patient did not arrive for appointment',
    color: 'rgb(var(--badge-error-text))',
    bgColor: 'rgb(var(--badge-error-bg))',
    borderColor: 'rgb(var(--badge-error-border))',
    canTransitionTo: ['rescheduled'], // Can reschedule no-shows
    isActive: false,
    priority: 7,
    allowManualSet: true,
    autoTrigger: {
      condition: 'grace-period-expired',
      delay: 20, // 20 minutes past appointment time
      triggerStatus: 'no-show'
    },
    requiresConfirmation: true,
    notificationTriggers: {
      patient: true, // Send no-show notification
      staff: true,
      system: true
    }
  },
  'rescheduled': {
    label: 'Rescheduled',
    shortLabel: 'Rescheduled',
    description: 'Appointment moved to a different time',
    color: 'rgb(var(--badge-warning-text))',
    bgColor: 'rgb(var(--badge-warning-bg))',
    borderColor: 'rgb(var(--badge-warning-border))',
    canTransitionTo: [], // Links to new appointment, this one is archived
    isActive: false,
    priority: 8,
    allowManualSet: true,
    requiresConfirmation: false, // Rescheduling flow handles confirmation
    notificationTriggers: {
      patient: true,
      staff: true,
      system: true
    }
  }
};

/**
 * Status Flow Scenarios
 */
export interface StatusFlowScenario {
  name: string;
  description: string;
  flow: AppointmentStatus[];
  isCommon: boolean;
}

export const STATUS_FLOW_SCENARIOS: StatusFlowScenario[] = [
  {
    name: 'Normal Flow',
    description: 'Standard appointment lifecycle',
    flow: ['pending', 'confirmed', 'checked-in', 'in-progress', 'completed'],
    isCommon: true
  },
  {
    name: 'Quick Check-in',
    description: 'Skip checked-in state for efficiency',
    flow: ['pending', 'confirmed', 'in-progress', 'completed'],
    isCommon: true
  },
  {
    name: 'Patient Cancellation',
    description: 'Patient cancels before appointment',
    flow: ['pending', 'confirmed', 'cancelled'],
    isCommon: true
  },
  {
    name: 'No-Show Detection',
    description: 'Patient doesn\'t arrive, auto-detected',
    flow: ['pending', 'confirmed', 'no-show'],
    isCommon: false
  },
  {
    name: 'No-Show Rescheduling',
    description: 'No-show patient reschedules',
    flow: ['pending', 'confirmed', 'no-show', 'rescheduled'],
    isCommon: false
  },
  {
    name: 'Emergency Cancellation',
    description: 'Last-minute cancellation during treatment',
    flow: ['pending', 'confirmed', 'in-progress', 'cancelled'],
    isCommon: false
  }
];

/**
 * Status validation and transition helpers
 */
export class AppointmentStatusManager {
  /**
   * Check if a status transition is valid
   */
  static canTransition(from: AppointmentStatus, to: AppointmentStatus): boolean {
    const fromConfig = APPOINTMENT_STATUS_CONFIG[from];
    return fromConfig.canTransitionTo.includes(to);
  }

  /**
   * Get all valid next statuses for current status
   */
  static getValidTransitions(currentStatus: AppointmentStatus): AppointmentStatus[] {
    return APPOINTMENT_STATUS_CONFIG[currentStatus].canTransitionTo;
  }

  /**
   * Check if status requires confirmation before changing
   */
  static requiresConfirmation(status: AppointmentStatus): boolean {
    return APPOINTMENT_STATUS_CONFIG[status].requiresConfirmation || false;
  }

  /**
   * Get status configuration
   */
  static getConfig(status: AppointmentStatus): AppointmentStatusConfig {
    return APPOINTMENT_STATUS_CONFIG[status];
  }

  /**
   * Get statuses that can be auto-triggered
   */
  static getAutoTriggerStatuses(): AppointmentStatus[] {
    return Object.entries(APPOINTMENT_STATUS_CONFIG)
      .filter(([_, config]) => config.autoTrigger)
      .map(([status, _]) => status as AppointmentStatus);
  }

  /**
   * Check if appointment should be auto-marked as no-show
   */
  static shouldAutoMarkNoShow(
    appointmentTime: Date, 
    currentStatus: AppointmentStatus,
    gracePeriodMinutes: number = 20
  ): boolean {
    if (currentStatus !== 'confirmed') return false;
    
    const now = new Date();
    const gracePeriodMs = gracePeriodMinutes * 60 * 1000;
    const graceExpiry = new Date(appointmentTime.getTime() + gracePeriodMs);
    
    return now > graceExpiry;
  }

  /**
   * Get status display information
   */
  static getDisplayInfo(status: AppointmentStatus, compact: boolean = false) {
    const config = APPOINTMENT_STATUS_CONFIG[status];
    return {
      label: compact ? (config.shortLabel || config.label) : config.label,
      color: config.color,
      bgColor: config.bgColor,
      borderColor: config.borderColor,
      description: config.description
    };
  }

  /**
   * Sort statuses by priority
   */
  static sortByPriority(statuses: AppointmentStatus[]): AppointmentStatus[] {
    return statuses.sort((a, b) => 
      APPOINTMENT_STATUS_CONFIG[a].priority - APPOINTMENT_STATUS_CONFIG[b].priority
    );
  }

  /**
   * Get active statuses (appointments that are still changeable)
   */
  static getActiveStatuses(): AppointmentStatus[] {
    return Object.entries(APPOINTMENT_STATUS_CONFIG)
      .filter(([_, config]) => config.isActive)
      .map(([status, _]) => status as AppointmentStatus);
  }

  /**
   * Get final statuses (appointments that are complete)
   */
  static getFinalStatuses(): AppointmentStatus[] {
    return Object.entries(APPOINTMENT_STATUS_CONFIG)
      .filter(([_, config]) => !config.isActive)
      .map(([status, _]) => status as AppointmentStatus);
  }

  /**
   * Check if status change should trigger notifications
   */
  static getNotificationTriggers(status: AppointmentStatus) {
    return APPOINTMENT_STATUS_CONFIG[status].notificationTriggers || {
      patient: false,
      staff: false,
      system: false
    };
  }
}

/**
 * Migration helpers for existing data
 */
export const STATUS_MIGRATION_MAP: Record<string, AppointmentStatus> = {
  // Django backend statuses (both underscore and hyphen formats)
  'pending': 'pending',
  'confirmed': 'confirmed',
  'checked_in': 'checked-in', // Backend underscore format
  'checked-in': 'checked-in', // Frontend hyphen format
  'in_progress': 'in-progress', // Backend underscore format  
  'in-progress': 'in-progress', // Frontend hyphen format
  'completed': 'completed',
  'cancelled': 'cancelled',
  'no_show': 'no-show', // Backend underscore format
  'no-show': 'no-show', // Frontend hyphen format
  'rescheduled': 'rescheduled',
  
  // Legacy/alternative formats
  'Scheduled': 'confirmed',
  'scheduled': 'confirmed', 
  'Confirmed': 'confirmed',
  'Pending': 'pending',
  'Cancelled': 'cancelled',
  'Completed': 'completed',
  'No-show': 'no-show',
  'No Show': 'no-show',
  'noshow': 'no-show',
  
  // Common variations that might exist
  'booked': 'pending',
  'reserved': 'pending',
  'active': 'in-progress',
  'started': 'in-progress',
  'ongoing': 'in-progress',
  'finished': 'completed',
  'done': 'completed',
  'missed': 'no-show',
  'absent': 'no-show'
};

/**
 * Helper to migrate old status to new status
 */
export function migrateStatus(oldStatus: string): AppointmentStatus {
  const migrated = STATUS_MIGRATION_MAP[oldStatus] || STATUS_MIGRATION_MAP[oldStatus.toLowerCase()];
  if (migrated) {
    return migrated;
  }
  
  // Default fallback
  console.warn(`Unknown appointment status: ${oldStatus}, defaulting to 'pending'`);
  return 'pending';
}

/**
 * Map professional status back to Django backend status
 */
export function mapStatusToBackend(professionalStatus: AppointmentStatus): string {
  const backendStatusMap: Record<AppointmentStatus, string> = {
    'pending': 'pending',
    'confirmed': 'confirmed', 
    'checked-in': 'checked_in', // Backend uses underscore
    'in-progress': 'in_progress', // Backend uses underscore
    'completed': 'completed',
    'cancelled': 'cancelled',
    'no-show': 'no_show', // Backend uses underscore
    'rescheduled': 'rescheduled'
  };
  
  return backendStatusMap[professionalStatus];
}

/**
 * Time-based status checking utilities
 */
export class StatusTimeChecker {
  /**
   * Check if appointment time has passed and status should be updated
   */
  static checkTimeBasedUpdates(
    appointmentDateTime: Date,
    currentStatus: AppointmentStatus
  ): AppointmentStatus | null {
    const now = new Date();
    
    // Only check active statuses
    if (!APPOINTMENT_STATUS_CONFIG[currentStatus].isActive) {
      return null;
    }
    
    // Check for no-show after grace period
    if (currentStatus === 'confirmed' || currentStatus === 'checked-in') {
      const gracePeriod = APPOINTMENT_STATUS_CONFIG['no-show'].autoTrigger?.delay || 20;
      const gracePeriodMs = gracePeriod * 60 * 1000;
      const graceExpiry = new Date(appointmentDateTime.getTime() + gracePeriodMs);
      
      if (now > graceExpiry) {
        return 'no-show';
      }
    }
    
    return null;
  }

  /**
   * Get human-readable time until status change
   */
  static getTimeUntilStatusChange(
    appointmentDateTime: Date,
    currentStatus: AppointmentStatus
  ): string | null {
    const config = APPOINTMENT_STATUS_CONFIG[currentStatus];
    if (!config.autoTrigger) return null;
    
    const now = new Date();
    const triggerTime = new Date(appointmentDateTime.getTime() + (config.autoTrigger.delay! * 60 * 1000));
    
    if (now >= triggerTime) return null;
    
    const diffMs = triggerTime.getTime() - now.getTime();
    const diffMinutes = Math.ceil(diffMs / (1000 * 60));
    
    if (diffMinutes < 60) {
      return `${diffMinutes} minutes`;
    } else {
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hours`;
    }
  }
}

/**
 * Export everything for easy imports
 */
export {
  AppointmentStatusManager as StatusManager,
  StatusTimeChecker as TimeChecker
};

export default APPOINTMENT_STATUS_CONFIG;